import { OTHER, type Block, type ClientFollowup, type Field, type Leaf } from "../lib/api";
import { btnGhost, btnPrimary, card } from "./ui";

// Última etapa fixa: "Conferir e publicar". Resumo de tudo que o cliente respondeu
// (por bloco, campos avulsos e sub-blocos), o follow-up, e o botão de publicar.

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const EMPTY = <span style={{ color: "var(--ink-dim)" }}>— não preenchido —</span>;

function fieldText(f: Field, answers: Record<string, any>): React.ReactNode {
  const v = answers[f.id] ?? f.default;
  const other = answers[`${f.id}:outro`];
  const label = (id: string) => (id === OTHER ? `Outros${other ? `: ${other}` : ""}` : f.options?.find((o) => o.id === id)?.label ?? id);
  switch (f.type) {
    case "select": return v ? label(v) : EMPTY;
    case "multi": return Array.isArray(v) && v.length ? v.map(label).join(", ") : EMPTY;
    case "bool": return v == null ? EMPTY : v ? (f.yes_text || "Sim") : (f.no_text || "Não");
    case "list": return Array.isArray(v) && v.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{v.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul> : EMPTY;
    case "faq": return Array.isArray(v) && v.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{v.map((x: any, i: number) => <li key={i}><b>{x?.q}</b>{x?.a ? ` — ${x.a}` : ""}</li>)}</ul> : EMPTY;
    case "hours": {
      const h = v && typeof v === "object" ? v : {};
      const parts = DAYS.map((d, i) => ({ d, rs: h[String(i)] ?? [] })).filter((x) => x.rs.length).map((x) => `${x.d} ${x.rs.map((r: string[]) => `${r[0]}–${r[1]}`).join(", ")}`);
      return parts.length ? parts.join(" · ") : EMPTY;
    }
    case "slider": return v == null || v === "" ? EMPTY : String(v);
    default: return v ? <span style={{ whiteSpace: "pre-wrap" }}>{String(v)}</span> : EMPTY;
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px minmax(0, 1fr)", gap: 12, padding: "10px 0", borderTop: "1px solid var(--line)", fontSize: 13 }}>
      <div style={{ color: "var(--ink-muted)", fontWeight: 500 }}>{label}</div>
      <div style={{ color: "var(--ink)", minWidth: 0, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

function leafRows(cells: Leaf[], answers: Record<string, any>, followup: ClientFollowup | null): React.ReactNode[] {
  return cells.map((c) => {
    if (c.kind === "field") return <Row key={c.id} label={c.field.label || c.field.key}>{fieldText(c.field, answers)}</Row>;
    if (c.kind === "tool") return <Row key={c.id} label={c.name}>{c.estoque ? (c.estoque.state === "connected" ? `Estoque conectado · ${c.estoque.vehicle_count} veículos` : "Estoque não conectado") : "Configurado"}</Row>;
    if (!followup) return null;
    const fu = followup;
    return (
      <Row key={c.id} label="Follow-up">
        {!fu.enabled ? "Desligado" : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span>Ligado · {fu.steps.length} follow{fu.steps.length === 1 ? "" : "s"}{fu.steps.length ? ` (${fu.steps.map((s) => (s.unit === "h" ? `${Math.round(s.after_minutes / 60)}h` : `${s.after_minutes}min`)).join(", ")})` : ""}</span>
            {fu.schedule.enabled && <span>Horário: {fu.schedule.days.map((d) => DAYS[d]).join(", ")} das {fu.schedule.start} às {fu.schedule.end}</span>}
            <span>Encerramento: {{ nenhuma: "sem ação", crm: "enviar para o CRM", notificar: "notificar o gerente" }[fu.end_action]}</span>
          </div>
        )}
      </Row>
    );
  });
}

export function ReviewStep({ blocks, answers, followup, dirty, publishing, onGoTo, onBack, onPublish }: {
  blocks: Block[]; answers: Record<string, any>; followup: ClientFollowup | null; dirty: boolean; publishing: boolean;
  onGoTo: (key: string) => void; onBack: () => void; onPublish: () => void;
}) {
  return (
    <div style={{ ...card, padding: "28px 32px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--brand)" }}>Última etapa</div>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 600, marginTop: 6, letterSpacing: "-.01em" }}>Conferir e publicar</h2>
        <p style={{ marginTop: 6, fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.5 }}>Revise o que você configurou. Publicar coloca tudo isso em produção no seu agente.</p>
      </div>
      {blocks.map((b, i) => (
        <section key={b.key} aria-labelledby={`rev-${b.key}`}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
            <h3 id={`rev-${b.key}`} style={{ fontSize: 15, fontWeight: 600 }}>{i + 1}. {b.title || b.key}</h3>
            <button type="button" onClick={() => onGoTo(b.key)} style={{ background: "none", border: "none", color: "var(--brand)", fontSize: 12, cursor: "pointer", padding: 0 }}>Editar</button>
          </div>
          {b.rows.flat().map((c) => c.kind === "group" ? (
            <div key={c.id} style={{ margin: "8px 0", padding: "4px 14px 0", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10 }}>
              {c.title && <div style={{ fontSize: 13, fontWeight: 600, padding: "8px 0 4px" }}>{c.title}</div>}
              {leafRows(c.rows.flat(), answers, followup)}
            </div>
          ) : leafRows([c], answers, followup))}
        </section>
      ))}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <button style={btnGhost} onClick={onBack}>Voltar</button>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {dirty && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>As alterações são salvas junto ao publicar.</span>}
          <button style={btnPrimary} disabled={publishing} onClick={onPublish}>{publishing ? "Publicando…" : "Publicar"}</button>
        </div>
      </div>
    </div>
  );
}
